// utils/auth.ts
// ACS-Light のロール権限（authoritylist）を UP-TONE 側で判定するためのユーティリティ。
// サーバー（/api2/authentication/getloginuserinfo）が返す authoritylist を保持し、画面・機能の表示制御に使う足場。

/**
 * 権限 ID 定数（ACS-Light の Constants.AUTH_* と対応）。
 */
export const AUTH = {
  // 画面アクセス権限
  SC_CUSTOMER_KARTE: 'AUTH_SC_CUSTOMER_KARTE',
  SC_CUSTOMER_EDIT: 'AUTH_SC_CUSTOMER_EDIT',
  // 機能制御権限
  FC_DELETE_CUSTOMER: 'AUTH_FC_DELETE_CUSTOMER',
  FC_DELETE_FOLLOW: 'AUTH_FC_DELETE_FOLLOW',
  FC_EDIT_CONTACTUSER: 'AUTH_FC_EDIT_CONTACTUSER',
  // 「全件編集」権限（権限なしの人は自担当のみ編集可）
  FC_EDIT_ALL_CUSTOMER: 'AUTH_FC_EDIT_ALL_CUSTOMER',
  FC_EDIT_ALL_FOLLOW: 'AUTH_FC_EDIT_ALL_FOLLOW',
} as const;

/** 権限判定に必要な最小の型。User など該当フィールドを含む任意のオブジェクトを受け付ける。 */
type AuthBearer = { authoritylist?: string[] | null; specialuser?: boolean | null } | null | undefined;

/**
 * 指定した権限 ID を保持しているかどうかを返す。
 *   - 特殊ユーザー（システム/企業管理者）は全許可
 *   - 通常ユーザーは authoritylist に含まれるかで判定
 * @param user authoritylist / specialuser を持つユーザー情報（null/undefined 可）
 * @param authId 権限 ID（例: AUTH.FC_DELETE_CUSTOMER）
 * @returns 権限あり: true / なし: false
 */
export function hasAuth(user: AuthBearer, authId: string): boolean {
  if (user?.specialuser) return true;
  return Array.isArray(user?.authoritylist) && user!.authoritylist!.includes(authId);
}

/**
 * 顧客/フォローの担当者番号(配列/JSON文字列/カンマ区切り文字列/数値)に対して、
 * 指定 userno が含まれているかを判定する。「自担当」かどうかの判定に使用。
 * AUTH_FC_EDIT_ALL_CUSTOMER / AUTH_FC_EDIT_ALL_FOLLOW なしのときに自分の担当のみ編集を許可するために使う。
 * @param contactuserno 担当者番号(any[] | string | number | null | undefined)
 * @param userno ログインユーザーの userno
 */
export function isContactUserOf(contactuserno: unknown, userno: number | null | undefined): boolean {
  if (userno == null) return false;
  if (contactuserno == null) return false;
  if (typeof contactuserno === 'number') return contactuserno === userno;
  if (Array.isArray(contactuserno)) {
    return contactuserno.some(v => Number(v) === userno);
  }
  if (typeof contactuserno === 'string') {
    try {
      const parsed = JSON.parse(contactuserno);
      if (Array.isArray(parsed)) return parsed.some(v => Number(v) === userno);
      return Number(parsed) === userno;
    } catch {
      return contactuserno.split(',').some(s => Number(s.trim()) === userno);
    }
  }
  return false;
}

/**
 * フォローの「自分のフォロー」判定。担当者または作成者なら true。
 * AUTH_FC_EDIT_ALL_FOLLOW / AUTH_FC_DELETE_FOLLOW なしのときに
 * 自分のフォローのみ編集・削除を許可するために使う。
 * @param contactuserno フォローの担当者番号
 * @param createuserno フォローの作成者番号
 * @param userno ログインユーザーの userno
 */
export function isFollowOwner(
  contactuserno: unknown,
  createuserno: unknown,
  userno: number | null | undefined
): boolean {
  return isContactUserOf(contactuserno, userno) || isContactUserOf(createuserno, userno);
}

/** フォロー権限判定に必要な最小のユーザー情報（authoritylist / specialuser / userno）。 */
export type FollowAuthUser =
  | { authoritylist?: string[] | null; specialuser?: boolean | null; userno?: number | null }
  | null
  | undefined;

/**
 * フォローを編集できるか。全フォロー編集権限(AUTH_FC_EDIT_ALL_FOLLOW)あり、または自分のフォロー（担当 or 作成）なら true。
 * フォロー編集可否の判定は必ずこの関数を通すこと（FollowHistoryPanel / FollowListModal で共通）。
 */
export function canEditFollow(user: FollowAuthUser, contactuserno: unknown, createuserno: unknown): boolean {
  return hasAuth(user, AUTH.FC_EDIT_ALL_FOLLOW) || isFollowOwner(contactuserno, createuserno, user?.userno);
}

/**
 * フォローを削除できるか。フォロー削除権限(AUTH_FC_DELETE_FOLLOW)あり、または自分のフォロー（担当 or 作成）なら true。
 * フォロー削除可否の判定は必ずこの関数を通すこと。
 */
export function canDeleteFollow(user: FollowAuthUser, contactuserno: unknown, createuserno: unknown): boolean {
  return hasAuth(user, AUTH.FC_DELETE_FOLLOW) || isFollowOwner(contactuserno, createuserno, user?.userno);
}
